<script>
  import Session from './Session.svelte'
  import { decodeWord } from './wordEngine.js'
  import { buildCustomWord } from './content.js'
  import { loadAudioManifest, unlockAudio } from './audio.js'
  import { UNITS } from './curriculum/b1.js'
  import { LESSONS } from './curriculum/ufli.js'
  import { ratingsByWord } from './ratings.js'

  // Index of every word used anywhere (B1 units + UFLI lessons), A→Z, with its
  // pronunciation rating. Tap a word to practise it (monkey/parrot) and keep going
  // through the following words; ‹ Index returns to the list any time.
  let { onexit } = $props()

  const CAP = 600        // max rows rendered
  const RUN = 250        // max words chained into one practice run

  const set = new Map() // lowercased -> original
  const add = (w) => { const k = String(w).toLowerCase(); if (!set.has(k)) set.set(k, w) }
  for (const u of UNITS) { u.words.forEach(add); u.tricky.forEach(add) }
  for (const l of LESSONS) { l.words.forEach(add); l.chains.flat().forEach(add); l.irregular.forEach(add) }
  const ALL = [...set.values()].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))

  let q = $state('')
  let ratings = $state({})
  let phase = $state('list') // list | session
  let steps = $state([])
  let sessionKey = $state(0)

  function loadRatings() { ratingsByWord().then((r) => (ratings = r)) }
  $effect(() => { loadRatings() })

  const filtered = $derived(q.trim() ? ALL.filter((w) => w.toLowerCase().includes(q.trim().toLowerCase())) : ALL)
  const shown = $derived(filtered.slice(0, CAP))
  const rateOf = (w) => ratings[w.toLowerCase()]
  const rateClass = (a) => (a < 2.5 ? 'lo' : a < 3.5 ? 'mid' : 'hi')

  // Load the shared phoneme library once so any word's blend plays without a
  // per-word audio fetch (words are decoded to the same ~44 phoneme codes).
  let phonemesLoaded = false
  async function ensurePhonemes() {
    if (phonemesLoaded) return
    const codes = new Set()
    for (const w of ALL) for (const s of decodeWord(w)) codes.add(s.code)
    const { manifest } = buildCustomWord('idx-phonemes', [...codes].map((c) => ({ text: c, code: c })))
    await loadAudioManifest(manifest)
    phonemesLoaded = true
  }

  async function practice(w) {
    unlockAudio()
    await ensurePhonemes()
    const list = filtered
    const start = Math.max(0, list.indexOf(w))
    const seq = list.slice(start, start + RUN)
    steps = seq.map((x) => ({ type: 'word', text: x, word: buildCustomWord(x, decodeWord(x)).word }))
    sessionKey += 1
    phase = 'session'
  }
  function backToList() { phase = 'list'; loadRatings() } // refresh ratings words may have gained
  function finishNote() { return '🎉 Reached the end of this run!' }
</script>

{#if phase === 'session'}
  {#key sessionKey}
    <Session title="🔤 Index" {steps} backLabel="‹ Index" onexit={backToList} onfinish={finishNote} source="index" />
  {/key}
{:else}
  <div class="index">
    <div class="toolbar"><button class="tb" onclick={onexit} aria-label="Back to menu">‹ Menu</button></div>
    <div class="head">
      <h2>🔤 Index</h2>
      <p>{filtered.length} of {ALL.length} words · tap to practise</p>
      <input class="search" type="search" placeholder="Search words…" bind:value={q} autocomplete="off" />
    </div>

    <div class="table">
      <div class="row header"><span class="w">Word</span><span class="rt">Sound rating</span></div>
      {#each shown as w (w)}
        <button class="row" onclick={() => practice(w)}>
          <span class="w">{w}</span>
          {#if rateOf(w)}
            <span class="rt {rateClass(rateOf(w).avg)}">★ {rateOf(w).avg.toFixed(1)} <small>({rateOf(w).count})</small></span>
          {:else}
            <span class="rt none">—</span>
          {/if}
        </button>
      {/each}
    </div>
    {#if filtered.length > CAP}<p class="more">Showing first {CAP} — refine your search to see the rest.</p>{/if}
  </div>
{/if}

<style>
  .index { position: fixed; inset: 0; overflow-y: auto; display: flex; flex-direction: column; align-items: center; gap: 1.4vh; padding: 7vh 2vh 4vh; box-sizing: border-box; }
  .toolbar { position: fixed; top: 1.2vh; left: 1.4vh; z-index: 25; }
  .tb { border: none; background: rgba(255,255,255,0.9); color: #2e5d1f; font-family: inherit; font-weight: 800; font-size: 1.7vh; padding: 0.8vh 1.5vh; border-radius: 999px; box-shadow: 0 3px 0 rgba(0,0,0,0.15); cursor: pointer; }
  .tb:active { transform: translateY(2px); box-shadow: 0 1px 0 rgba(0,0,0,0.15); }
  .head { text-align: center; color: #fffef0; display: flex; flex-direction: column; align-items: center; gap: 0.8vh; }
  .head h2 { margin: 0; font-size: 3vh; text-shadow: 0 2px 0 rgba(0,0,0,0.15); }
  .head p { margin: 0; font-weight: 700; opacity: 0.95; font-size: 1.7vh; }
  .search { font-family: inherit; font-size: 2.1vh; font-weight: 700; color: #2e5d1f; border: none; border-radius: 999px; padding: 1vh 2vh; width: min(80vw, 420px); box-shadow: inset 0 2px 5px rgba(0,0,0,0.15); }

  .table { width: min(620px, 96vw); display: flex; flex-direction: column; gap: 0.5vh; }
  .row { display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 1.2vh; width: 100%; border: none; background: #fffef0; border-radius: 10px; padding: 1.1vh 1.6vh; cursor: pointer; text-align: left; box-shadow: 0 2px 0 rgba(0,0,0,0.1); font-family: inherit; }
  .row:active:not(.header) { transform: translateY(1px); }
  .row.header { background: transparent; box-shadow: none; cursor: default; padding-bottom: 0; }
  .w { color: #2e5d1f; font-weight: 800; font-size: 2.2vh; }
  .row.header .w, .row.header .rt { color: #fffef0; font-size: 1.5vh; font-weight: 800; }
  .rt { font-weight: 800; font-size: 1.8vh; text-align: right; }
  .rt.none { color: #c9c9c9; }
  .rt.lo { color: #c0392b; }
  .rt.mid { color: #b3701b; }
  .rt.hi { color: #2e7d32; }
  .rt small { font-weight: 700; opacity: 0.7; }
  .more { color: #fffef0; font-weight: 700; opacity: 0.9; }
</style>
